import { PortalColorSchemas, PortalThemeColors } from './portal-colors';
import { TailwindBorder, TailwindClassName, TailwindRounded, TailwindShadow } from './tailwind-class-name';
import { DeepPartial } from './utils';

export enum PortalSearchPosition {
  NONE = 'none',
  SIDEBAR = 'sidebar',
  HEADER_ICON = 'header_icon',
  HEADER_HERO = 'header_hero',
}

export type PortalHeaderType = 'hidden' | 'menu-right' | 'menu-center' | 'menu-center-search' | 'menu-right-search';

export type PortalHeroType =
  | 'empty'
  | 'search-and-greetings'
  | 'button-left'
  | 'right-side'
  | 'headline'
  | 'counsulting'
  | 'events-1'
  | 'left-side'
  | 'carousel';

export enum PortalHeaderWidth {
  WIDE = 'wide',
  SHORT = 'short',
}

export enum PortalHeaderMenuAlign {
  RIGHT = 'right',
  CENTER = 'center',
}

export enum PortalSidebarType {
  STANDARD = 'standard',
  MULTI = 'multi',
}

export enum PortalFooterType {
  HIGH = 'high',
  LOW = 'low',
}

export enum PortalFooterWidth {
  WIDE = 'wide',
  SHORT = 'short',
}

export enum PortalClassState {
  HOVER = 'hover',
  FOCUS = 'focus',
  ACTIVE = 'active',
  PLACEHOLDER = 'placeholder',
}

export enum PortalAllMediaClasses {
  BASE = 'base',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  XL2 = '2xl',
}

export enum PortalClassMediaBasic {
  BASE = PortalAllMediaClasses.BASE,
  MD = PortalAllMediaClasses.MD,
  LG = PortalAllMediaClasses.LG,
}

export enum PortalClassMediaAdditional {
  SM = PortalAllMediaClasses.SM,
  XL = PortalAllMediaClasses.XL,
}

export enum PortalBreadcrumbsPosition {
  INSIDE_CONTENT = 'inside_content',
  OUTSIDE_CONTENT = 'outside_content',
}

export type PortalClassMedia = PortalClassMediaBasic | PortalClassMediaAdditional;

export type PortalClassStateOrMedia = PortalClassState | PortalClassMedia;

export interface PortalHeaderConfig {
  type: PortalHeaderType;
  sticky: boolean;
  width: PortalHeaderWidth;
  className: TailwindClassName[];
  search: {
    show: boolean;
  };
  notifications: {
    show: boolean;
  };
  menu: {
    show: boolean;
    align: PortalHeaderMenuAlign;
  };
  logo: {
    show: boolean;
    className: TailwindClassName[];
  };
}

export interface PortalHeroConfig {
  show: boolean;
  type: PortalHeroType;
  className: TailwindClassName[];
  blocks: {
    rtl: boolean;
  };
}

export interface PortalFooterConfig {
  type: PortalFooterType;
  width: PortalFooterWidth;
  sticky: boolean;
}

export interface PortalSearchConfig {
  position: PortalSearchPosition;
}

export interface PortalSidebarConfig {
  show: boolean;
  homePage: boolean;
  deepPage: boolean;
  minimized: boolean;
  type: PortalSidebarType;
}

export interface PortalSecondbarConfig {
  show: boolean;
  homePage: boolean;
  deepPage: boolean;
}

export interface PortalBreadcrumbsConfig {
  show: boolean;
  homePage: boolean;
  position: PortalBreadcrumbsPosition;
}

export interface PortalLogoConfig {
  headerLogo: string;
  footerLogo: string;
}

export type PortalThemeCustomColors = Partial<Omit<PortalThemeColors, 'name'>> & Pick<PortalThemeColors, 'key'>;

export type PortalColorsConfig = {
  [key in PortalColorSchemas]: PortalThemeCustomColors;
};

export interface PortalStyleConfig {
  radius: {
    className: TailwindRounded[];
  };
  border: {
    className: TailwindBorder[];
  };
  shadow: {
    className: TailwindShadow[];
  };
}

export interface PortalContentConfig {
  cols: {
    [key in PortalClassMediaBasic]: number;
  };
  gap: {
    [key in PortalClassMediaBasic]: number;
  };
}

export interface PortalThemeConfig {
  header: PortalHeaderConfig;
  hero: PortalHeroConfig;
  footer: PortalFooterConfig;
  search: PortalSearchConfig;
  sidebar: PortalSidebarConfig;
  secondbar: PortalSecondbarConfig;
  breadcrumbs: PortalBreadcrumbsConfig;
  style: PortalStyleConfig;
  colors: PortalColorsConfig;
  content: PortalContentConfig;
}

export type PortalThemeConfigPartial = DeepPartial<PortalThemeConfig>;

export type PortalThemeExportConfig = {
  version: number;
  createdAt: number;
  portalId: string;
  theme: PortalThemeConfigPartial;
};

export enum BorderPosition {
  TOP = 'border-t',
  BOTTOM = 'border-b',
  RIGHT = 'border-r',
  LEFT = 'border-l',
  HORIZONTAL = 'border-x',
  VERTICAL = 'border-y',
}
